import React, { useState, useMemo, useEffect, useCallback } from 'react';
import Table, { TableColumn } from '../../components/Table/Table';
import { PersonnelCoastGuardPermit } from '../../types';
import PersonnelPermitForm from '../../components/HR/PersonnelPermitForm';
import { PageHeader, FilterBar, ErrorState, StatsCards, Pagination } from '../../components/Common';
import type { FilterConfig } from '../../components/Common';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { usePersonnelPermits, useCreatePersonnelPermit, useUpdatePersonnelPermit, useDeletePersonnelPermit } from '../../hooks/usePersonnelPermits';
import { useLocations } from '../../hooks/useLocations';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { usePermitStats, isoDaysAhead } from '../../hooks/usePermitStats';
import { personnelPermitsService } from '../../services';
import { useToast } from '../../contexts/ToastContext';

const PersonnelCoastGuardPermits: React.FC = () => {
  const { showToast } = useToast();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [branchFilter, setBranchFilter] = useState<string>('all');
  const [locationFilter, setLocationFilter] = useState<string>('all');
  const [expiryFilter, setExpiryFilter] = useState<string>('all');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');

  const debouncedSearch = useDebouncedValue(searchTerm);

  // All filters run server-side so search and pagination cover the whole dataset
  const serverFilters = useMemo(() => {
    const conditions: any[] = [];
    if (debouncedSearch) {
      conditions.push({ $or: [
        { employeeName: { $containsi: debouncedSearch } },
        { branchName: { $containsi: debouncedSearch } },
        { locationAddress: { $containsi: debouncedSearch } },
        { permitType: { $containsi: debouncedSearch } },
        { employee: { name: { $containsi: debouncedSearch } } },
        { employee: { code: { $containsi: debouncedSearch } } },
      ] });
    }
    if (branchFilter !== 'all') conditions.push({ branchName: { $eq: branchFilter } });
    if (locationFilter !== 'all') conditions.push({ locationAddress: { $eq: locationFilter } });
    if (expiryFilter === 'expired') {
      conditions.push({ $or: [{ expiryDate: { $lt: isoDaysAhead(0) } }, { expiryDate: { $null: true } }] });
    } else if (expiryFilter === 'expiring_soon') {
      conditions.push({ expiryDate: { $gte: isoDaysAhead(0), $lte: isoDaysAhead(30) } });
    } else if (expiryFilter === 'valid') {
      conditions.push({ expiryDate: { $gt: isoDaysAhead(30) } });
    }
    if (dateFromFilter) conditions.push({ issueDate: { $gte: dateFromFilter } });
    if (dateToFilter) conditions.push({ issueDate: { $lte: dateToFilter } });
    return conditions.length ? { $and: conditions } : undefined;
  }, [debouncedSearch, branchFilter, locationFilter, expiryFilter, dateFromFilter, dateToFilter]);

  const filtersKey = JSON.stringify(serverFilters || {});
  useEffect(() => { setPage(1); }, [filtersKey]);

  const queryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { page, pageSize },
    ...(serverFilters ? { filters: serverFilters } : {}),
  }), [page, pageSize, serverFilters]);

  const { data: permits = [], meta, isLoading, error, refetch } = usePersonnelPermits(queryOptions);
  const totalCount = meta?.pagination?.total ?? permits.length;

  const locationsOptions = useMemo(() => ({ pagination: { pageSize: 100 } }), []);
  const { data: locations = [] } = useLocations(locationsOptions);

  const createMutation = useCreatePersonnelPermit(() => {
    showToast('Personnel permit created successfully', 'success');
    refetch();
    setIsFormOpen(false);
    setSelectedPermit(undefined);
  });

  const updateMutation = useUpdatePersonnelPermit(() => {
    showToast('Personnel permit updated successfully', 'success');
    refetch();
    setIsFormOpen(false);
    setSelectedPermit(undefined);
  });

  const deleteMutation = useDeletePersonnelPermit(() => {
    showToast('Personnel permit deleted successfully', 'success');
    refetch();
  });

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedPermit, setSelectedPermit] = useState<PersonnelCoastGuardPermit | undefined>();

  const uniqueBranches = Array.from(new Set(permits.map(p => p.branchName).filter(Boolean)));
  const uniqueLocations = useMemo(() =>
    Array.from(new Set(locations.map((l: any) => l.address).filter(Boolean))),
  [locations]);

  // Status counts come from the server so they cover the whole filtered dataset
  const permitStats = usePermitStats(personnelPermitsService, serverFilters, 'personnel_permit_status');
  const stats = { total: totalCount, ...permitStats };

  // Stored status is only as fresh as the last save — a past expiry date
  // always means expired, whatever the enum still says.
  const displayStatus = useCallback((permit: PersonnelCoastGuardPermit): string => {
    if (permit.expiryDate && permit.expiryDate < isoDaysAhead(0) && permit.status !== 'suspended') {
      return 'expired';
    }
    return permit.status;
  }, []);

  const filters: FilterConfig[] = useMemo(() => [
    { key: 'branch', label: 'Vessel/Headquarters', type: 'select', value: branchFilter, onChange: setBranchFilter, options: [{ value: 'all', label: 'All Vessels/HQ' }, ...uniqueBranches.map(b => ({ value: b, label: b }))] },
    { key: 'location', label: 'Location', type: 'select', value: locationFilter, onChange: setLocationFilter, options: [{ value: 'all', label: 'All Locations' }, ...uniqueLocations.map(l => ({ value: l, label: l }))] },
    { key: 'expiry', label: 'Expiry Status', type: 'select', value: expiryFilter, onChange: setExpiryFilter, options: [{ value: 'all', label: 'All' }, { value: 'valid', label: 'Valid (30+ days)' }, { value: 'expiring_soon', label: 'Expiring Soon (30 days)' }, { value: 'expired', label: 'Expired' }] },
    { key: 'dateFrom', label: 'Issue Date From', type: 'date', value: dateFromFilter, onChange: setDateFromFilter },
    { key: 'dateTo', label: 'Issue Date To', type: 'date', value: dateToFilter, onChange: setDateToFilter },
  ], [branchFilter, locationFilter, expiryFilter, dateFromFilter, dateToFilter, uniqueBranches, uniqueLocations]);

  // Exports cover the entire filtered dataset, not just the visible page
  const fetchAllFiltered = useCallback(async (): Promise<PersonnelCoastGuardPermit[]> => {
    const response: any = await personnelPermitsService.getAll({
      sort: [{ field: 'createdAt', order: 'desc' as const }],
      ...(serverFilters ? { filters: serverFilters } : {}),
    });
    return response.data || [];
  }, [serverFilters]);

  const permitRow = (permit: PersonnelCoastGuardPermit) => ({
    'Employee Name': permit.employeeName, 'Vessel/Headquarters': permit.branchName, 'Location': permit.locationAddress,
    'Issue Date': permit.issueDate || '-', 'Expiry Date': permit.expiryDate || '-',
    'Expiry Status': displayStatus(permit).charAt(0).toUpperCase() + displayStatus(permit).slice(1),
    'Notes': permit.notes || '', 'Created Date': permit.createdAt ? new Date(permit.createdAt).toLocaleDateString() : '-',
  });

  const handleExport = async () => {
    const all = await fetchAllFiltered();
    exportToExcel(all.map(permitRow), `personnel_permits_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = async () => {
    const all = await fetchAllFiltered();
    printAsExcel(all.map(permitRow), 'Personnel Coast Guard Permits');
  };

  const handleEdit = (permit: PersonnelCoastGuardPermit) => { setSelectedPermit(permit); setIsFormOpen(true); };
  const handleDelete = async (permit: PersonnelCoastGuardPermit) => {
    if (window.confirm(`Are you sure you want to delete the ${permit.permitType} permit for ${permit.employeeName}?`)) {
      try { await deleteMutation.execute(permit); } catch (e) { console.error('Delete personnel permit error:', e); }
    }
  };
  const handleAdd = () => { setSelectedPermit(undefined); setIsFormOpen(true); };
  const handleSubmit = async (data: Partial<PersonnelCoastGuardPermit>) => {
    if (selectedPermit) {
      try { await updateMutation.execute({ id: selectedPermit.id, data: { ...selectedPermit, ...data } }); } catch (e) { console.error('Update personnel permit error:', e); }
    } else {
      try { await createMutation.execute(data); } catch (e) { console.error('Create personnel permit error:', e); }
    }
  };
  const handleCancel = () => { setIsFormOpen(false); setSelectedPermit(undefined); };
  const onClearFilters = () => { setSearchTerm(''); setBranchFilter('all'); setLocationFilter('all'); setExpiryFilter('all'); setDateFromFilter(''); setDateToFilter(''); };

  const columns: TableColumn[] = [
    { key: 'employeeName', label: 'Employee', sortable: true },
    { key: 'issueDate', label: 'Issue Date', sortable: true, render: (v: string) => v ? new Date(v).toLocaleDateString() : '-' },
    { key: 'expiryDate', label: 'Expiry Date', sortable: true, render: (v: string) => v ? new Date(v).toLocaleDateString() : '-' },
    { key: 'branchName', label: 'Vessel/Headquarters', sortable: true },
    { key: 'locationAddress', label: 'Location', sortable: true },
    { key: 'status', label: 'Expiry Status', sortable: true, render: (_: string, item: PersonnelCoastGuardPermit) => {
      const v = displayStatus(item);
      return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${v === 'active' ? 'bg-green-100 text-green-800' : v === 'expired' ? 'bg-red-100 text-red-800' : v === 'suspended' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`}>{v.charAt(0).toUpperCase() + v.slice(1)}</span>
      );
    } }
  ];

  if (isLoading && !meta) return <div className="flex items-center justify-center h-64"><div className="text-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ocean-600 mx-auto mb-4" /><p className="text-gray-600">Loading personnel permits...</p></div></div>;
  if (error) return <ErrorState message="Error loading personnel permits" onRetry={refetch} entityName="personnel permits" />;

  return (
    <div className="space-y-6">
      <PageHeader title="Personnel Coast Guard Permits" description="Manage personnel maritime permits and certifications" />
      <StatsCards stats={[{ label: 'Total Permits', value: stats.total, color: 'blue' }, { label: 'Active Permits', value: stats.active, color: 'green' }, { label: 'Expiring Soon', value: stats.expiringSoon, color: 'yellow' }, { label: 'Expired', value: stats.expired, color: 'red' }]} />
      <FilterBar searchTerm={searchTerm} onSearchChange={setSearchTerm} searchPlaceholder="Search permits..." filters={filters} onClearFilters={onClearFilters} totalCount={totalCount} filteredCount={totalCount} entityName="permits" onAdd={handleAdd} addLabel="Add Permit" onExport={handleExport} onPrint={handlePrint} />
      <div className="card">
        <div className={isLoading ? 'opacity-50 pointer-events-none' : ''}>
          <Table columns={columns} data={permits} onEdit={handleEdit} onDelete={handleDelete} showAddButton={false} searchable={false} />
        </div>
        <Pagination page={page} pageSize={pageSize} total={totalCount} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} isLoading={isLoading} />
      </div>
      {isFormOpen && <PersonnelPermitForm permit={selectedPermit} onSubmit={handleSubmit} onCancel={handleCancel} />}
    </div>
  );
};

export default PersonnelCoastGuardPermits;
